<template>
  <div class="flex h-dvh flex-col overscroll-none sm:flex-row">
    <NavMenu
      :logo-url="authStore.loggedInUser?.selectedOrg?.logoUrl"
      :nav-items="navItems"
      :order-requests-count="ordersByOrgCount"
      :claim-requests-count="claimsByOrgCount"
      :return-requests-count="returnsByOrgCount"
      :org-name="orgName"
      :payment-requests-count="paymentRequestsByOrgCount"
      :credits-count="creditsTabShown ? creditsByOrgCount : 0"
      :client-variation-count="clientVariationsByOrgCount"
      :user-first-name="firstName"
      :user-name="userName"
      :user-email="userEmail"
      :show-settings-items="showSettingsItems"
    />

    <!--
      The bar slides up out of the way as the user scrolls **down** and returns on the way **up**,
      reclaiming its height while they read (CPTO, 2026-09-11).

      It used to do this by accident: the bar sat outside the bounded box, so the document was one
      nav-bar taller than the viewport and scrolling simply pushed it off. That same overflow is
      what left every `sticky bottom-0` footer unstuck, so bounding the layout necessarily took the
      behaviour with it. This puts it back deliberately — and measured, rather than against the
      60px the old `calc()` assumed.

      `motion-reduce:transition-none` keeps the state change and drops the movement.
    -->
    <div
      ref="mobileNavBandRef"
      class="shrink-0 transition-[margin] duration-200 ease-out motion-reduce:transition-none sm:hidden"
      :style="mobileNavBandStyle"
    >
      <MobileNavigation
        v-if="!isMobileLandscape"
        :logo-url="authStore.loggedInUser?.selectedOrg?.logoUrl"
        :nav-items="navItems"
        :order-requests-count="ordersByOrgCount"
        :claim-requests-count="claimsByOrgCount"
        :return-requests-count="returnsByOrgCount"
        :org-name="orgName"
        :payment-requests-count="paymentRequestsByOrgCount"
        :credits-count="creditsTabShown ? creditsByOrgCount : 0"
        :client-variation-count="clientVariationsByOrgCount"
        :user-first-name="firstName"
        :user-email="userEmail"
        :user-name="userName"
        :show-settings-items="showSettingsItems"
      />
    </div>

    <div
      id="layout-container"
      class="bg-background-light-200 flex min-h-0 flex-1 flex-col overflow-hidden"
    >
      <slot />
    </div>
  </div>
</template>

<script lang="ts" setup>
import router from '@/router';
import { storeToRefs } from 'pinia';
import { computed, onMounted, onUnmounted, ref, toRefs } from 'vue';

import { useAuthStore } from '@/stores/auth.store';
import { useOrgStore } from '@/stores/organisations.store';
import { useClientVariationStore } from '@/stores/client-variation.store';
import { useCreditRequestStore } from '@/stores/credit-requests.store';

import NavMenu from '@/components/UiLibrary/Menu/NavMenu.vue';
import MobileNavigation from '@/components/Navigation/MobileNavigation.vue';
import { useHideOnScroll } from '@/composables/use-hide-on-scroll';
import { useNavItems } from '@/composables/use-nav-items';

import { useOrdersStore } from '@/stores/orders.store';
import { useTotalsStore } from '@/stores/utils/totals.store';
import { useCan } from '@/composables/use-can';
import { Actions, Subjects } from '@boqnet/permissions';
import { selectedOrgId } from '@/common/utils/role.utils';

import { useReturnsStore } from '@/stores/returns.store';
import { userIdentity } from '@/common/utils/user-identity';

const orgStore = useOrgStore();
const authStore = useAuthStore();
const { can } = useCan();
const ordersStore = useOrdersStore();
const returnStore = useReturnsStore();
const clientVariationStore = useClientVariationStore();
const creditRequestStore = useCreditRequestStore();

const { loggedInUser } = storeToRefs(authStore);
const {
  paymentRequestsByOrgCount,
  ordersByOrgCount,
  claimsByOrgCount,
  returnsByOrgCount,
  clientVariationsByOrgCount,
  creditsByOrgCount,
} = toRefs(useTotalsStore());

// --- Accounts nav badge: payment requests (+ credits for non-AP roles) — PE-213 ---
// Whether the Accounts surface includes the Credits tab for this user. Derived from
// ROLES (NOT can()), stable across navigation. Mirrors DefaultLayout.
// Context: org finance, org admin and the BOQ tier keep the Accounts Payable tab and never get a
// separate Credits tab, so the audience is "holds credits, but has no AP tab to hold them" rather
// than a role list (PE-213).
const creditsTabShown = computed(
  () => can(Actions.LIST, Subjects.CREDIT) && !can(Actions.LIST, Subjects.ACCOUNTS_PAYABLE),
);

// Fetch the org-scoped pending credits count into the totals store (`creditsByOrgCount`)
// for the Accounts nav badge. Org-scoped → no accountsPayableId; the BE scopes the
// result to the user's sites/projects. Mirrors AccountsLayout's credits-count fetch.
const fetchCreditsNavCount = async () => {
  // Only fetch when the Credits tab applies to this user (the badge is gated the
  // same way), so non-Credits/flag-off users don't issue an org-scoped credits
  // request. Snapshot/restore the shared store filters so this nav-level fetch
  // doesn't leave creditRequestStore scoped for other views. (PE-213)
  if (!creditsTabShown.value || !selectedOrgId.value) return;

  const previousFilters = { ...creditRequestStore.filters };
  try {
    creditRequestStore.filters.organisationId = selectedOrgId.value;
    creditRequestStore.filters.accountsPayableId = null;
    await creditRequestStore.fetchCount();
  } finally {
    Object.assign(creditRequestStore.filters, previousFilters);
  }
};

const orgName = computed(() => loggedInUser.value?.selectedOrg?.name || '');

const showSettingsItems = computed(() => userIdentity.seesSettingsMenu());

const { navItems } = useNavItems();

// The avatar derives its own initials, so it wants the name rather than an "AB" computed here —
// feeding it initials would have it take the first letter of those and show one.
const userName = computed(() =>
  [loggedInUser.value?.firstName, loggedInUser.value?.lastName].filter(Boolean).join(' '),
);

const firstName = computed(() => loggedInUser.value?.firstName);

const userEmail = computed(() => loggedInUser.value?.email);

// The band the bar sits in, not the bar itself — the component already owns a ref here.
const { targetRef: mobileNavBandRef, targetStyle: mobileNavBandStyle } = useHideOnScroll({
  // Navigation can swap the inner scroller while this layout stays mounted, and a short
  // destination page emits no scroll event — so without this the bar can stay hidden on a page
  // with nothing left to scroll.
  resetOn: () => router.currentRoute.value.fullPath,
});

const isMobileLandscape = ref(false);

const checkOrientation = () => {
  const width = window.innerWidth;
  const height = window.innerHeight;

  isMobileLandscape.value = width > height && width < 1024;
};

onUnmounted(() => {
  window.removeEventListener('resize', checkOrientation);
});

onMounted(() => {
  setTimeout(() => {
    if (!userIdentity.isBareOrgMember() && userIdentity.seesStaffNavigation()) {
      orgStore.getPaymentRequestCount(selectedOrgId.value!, {
        status: 'Requested',
      });
      ordersStore.getOrderTotals({ orgId: selectedOrgId.value!, type: 'Order' }, 'orders');
      ordersStore.getOrderTotals({ orgId: selectedOrgId.value!, type: 'Claim' }, 'claims');
      orgStore.getOrgDetails();
      fetchCreditsNavCount();
      returnStore.getReturnsCount({
        organisationId: selectedOrgId.value!,
      });
    }
  }, 500);
  if (userIdentity.isClientAnywhere()) {
    clientVariationStore.getClientVariationCount(selectedOrgId.value!);
  }
  window.addEventListener('resize', checkOrientation);
  checkOrientation();
});
</script>
